import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { Logo } from '@/components/generals/logo'
import { buttonClassName } from '@/components/generals/button'
import { consortiumCategories } from '@/constants/consorcios'
import { footerNavigation } from '@/constants/navigation'
import { siteConfig } from '@/constants/site'

interface SiteFooterProps {
    ctaHref: string
}

const footerLinkClassName = 'rounded text-sm text-primary-foreground/70 transition-colors hover:text-primary-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold'

export const SiteFooter: FC<SiteFooterProps> = ({ ctaHref }): ReactNode => {
    const year = new Date().getFullYear()

    return (
        <footer className='bg-ink text-primary-foreground'>
            <div className='mx-auto max-w-7xl px-5 pb-10 pt-16 sm:px-8 lg:pt-20'>
                <div className='grid gap-12 md:grid-cols-2 lg:grid-cols-[1.5fr_1fr_1fr_1fr]'>
                    <div>
                        <Link
                            href='/'
                            aria-label='Chave Consórcios, página inicial'
                            className='inline-flex rounded-xl focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold'
                        >
                            <Logo tone='light' />
                        </Link>

                        <p className='mt-6 max-w-sm text-sm leading-relaxed text-primary-foreground/70'>
                            Consultoria em consórcio de imóveis, veículos e serviços. Ajudamos você a escolher o grupo certo e acompanhamos cada passo até a contemplação.
                        </p>

                        <Link href={ctaHref} className={buttonClassName({ variant: 'gold', className: 'mt-8' })}>
                            Receber uma proposta
                            <ArrowRight aria-hidden='true' className='size-4' />
                        </Link>
                    </div>

                    <div>
                        <h2 className='text-xs font-bold uppercase tracking-[0.22em] text-gold'>
                            Consórcios
                        </h2>

                        <ul className='mt-5 grid gap-3'>
                            {consortiumCategories.map(category => (
                                <li key={category.slug}>
                                    <Link href={`/consorcio/${category.slug}`} className={footerLinkClassName}>
                                        {category.title}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <h2 className='text-xs font-bold uppercase tracking-[0.22em] text-gold'>
                            Navegação
                        </h2>

                        <ul className='mt-5 grid gap-3'>
                            {footerNavigation.map(item => (
                                <li key={item.href}>
                                    <Link href={item.href} className={footerLinkClassName}>
                                        {item.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <h2 className='text-xs font-bold uppercase tracking-[0.22em] text-gold'>
                            Atendimento
                        </h2>

                        <ul className='mt-5 grid gap-3'>
                            <li>
                                <Link href={ctaHref} className={footerLinkClassName}>
                                    Pedir uma proposta
                                </Link>
                            </li>

                            <li>
                                <Link href='/privacidade' className={footerLinkClassName}>
                                    Política de privacidade
                                </Link>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className='mt-16 border-t border-ink-line pt-8'>
                    <p className='max-w-4xl text-xs leading-relaxed text-primary-foreground/55'>
                        A {siteConfig.name} atua na consultoria e na intermediação de cotas de consórcio de administradoras autorizadas e fiscalizadas pelo Banco Central do Brasil. Taxas, prazos, índices de reajuste e regras de contemplação são definidos por cada administradora e informados no contrato do grupo. As simulações deste site são estimativas e não constituem oferta.
                    </p>

                    <div className='mt-6 flex flex-col gap-3 text-xs text-primary-foreground/55 sm:flex-row sm:items-center sm:justify-between'>
                        <p>
                            © <span suppressHydrationWarning>{year}</span> {siteConfig.name}. Todos os direitos reservados.
                        </p>

                        <Link href='/privacidade' className={footerLinkClassName}>
                            Política de privacidade
                        </Link>
                    </div>
                </div>
            </div>
        </footer>
    )
}
